/**
 * Axios 全局配置模块
 *
 * 在 main.ts 中导入此模块会自动初始化 axios 全局配置
 * 包括：baseURL、拦截器、错误处理等
 */

import axios, { type AxiosError, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios'
import { ElMessage } from 'element-plus'
import router from '@/router'
import { httpLogger } from './http-logger'

// 根据环境变量配置 baseURL
const getBaseURL = () => {
  // 优先使用 VITE_APP_API_HOST，兼容旧的 VITE_API_BASE_URL
  const apiHost = import.meta.env.VITE_APP_API_HOST
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL

  if (apiHost) {
    return apiHost
  }

  if (apiBaseUrl) {
    return `${apiBaseUrl}`
  }

  return '/'
}

/**
 * 检查对象中是否包含 File 或 File[] 类型的值
 */
function hasFileInData(data: any): boolean {
  if (!data || typeof data !== 'object') {
    return false
  }

  for (const key in data) {
    const value = data[key]
    if (value instanceof File) {
      return true
    }
    if (Array.isArray(value) && value.length > 0 && value[0] instanceof File) {
      return true
    }
  }
  return false
}

/**
 * 将对象转换为 FormData
 * 支持嵌套对象和数组
 */
function objectToFormData(data: any): FormData {
  const formData = new FormData()

  function appendFormData(key: string, value: any) {
    if (value === null || value === undefined) {
      return
    }

    if (value instanceof File) {
      formData.append(key, value)
    } else if (Array.isArray(value)) {
      value.forEach((item, index) => {
        if (item instanceof File) {
          // 文件数组使用相同的 key，后端会接收为数组
          formData.append(key, item)
        } else if (typeof item === 'object' && item !== null) {
          // 对象数组序列化为 JSON
          appendFormData(`${key}[${index}]`, JSON.stringify(item))
        } else {
          appendFormData(`${key}[${index}]`, item)
        }
      })
    } else if (typeof value === 'object' && !(value instanceof Date)) {
      // 普通对象序列化为 JSON
      formData.append(key, JSON.stringify(value))
    } else if (value instanceof Date) {
      // 日期转换为 ISO 字符串
      formData.append(key, value.toISOString())
    } else {
      // 基本类型直接添加
      formData.append(key, String(value))
    }
  }

  for (const key in data) {
    appendFormData(key, data[key])
  }

  return formData
}

// Token 刷新状态管理
let isRefreshing = false
let refreshPromise: Promise<string> | null = null

// 刷新 token
async function refreshToken(): Promise<string> {
  // 如果正在刷新，返回同一个 Promise
  if (isRefreshing && refreshPromise) {
    return refreshPromise
  }

  const refreshToken = localStorage.getItem('refresh_token')
  if (!refreshToken) {
    throw new Error('No refresh token')
  }

  isRefreshing = true
  refreshPromise = (async () => {
    try {
      const response = await axios.post('/api/v1/auth/refresh', {
        refresh_token: refreshToken,
      })

      const { access_token, refresh_token: new_refresh_token } = response.data

      // 更新 token
      localStorage.setItem('access_token', access_token)
      if (new_refresh_token) {
        localStorage.setItem('refresh_token', new_refresh_token)
      }

      return access_token
    } catch (error) {
      // 刷新失败，清除所有 token
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      throw error
    } finally {
      isRefreshing = false
      refreshPromise = null
    }
  })()

  return refreshPromise
}

/**
 * 初始化 axios 全局配置
 * 此函数在模块加载时自动执行
 */
function setupAxios() {
  // 配置 axios 默认实例
  axios.defaults.baseURL = getBaseURL()
  axios.defaults.timeout = import.meta.env.VITE_API_TIMEOUT
    ? parseInt(import.meta.env.VITE_API_TIMEOUT)
    : 30000

  // 请求拦截器
  axios.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // 记录请求日志
      httpLogger.request(config)

      // 从 localStorage 获取 token
      const token = localStorage.getItem('access_token')
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }

      // 智能处理 Content-Type 和数据转换
      if (!config.headers['Content-Type']) {
        // 检查数据中是否包含 File 类型
        if (config.data && hasFileInData(config.data)) {
          // 自动转换为 FormData
          config.data = objectToFormData(config.data)
          // 不设置 Content-Type，让浏览器自动处理
          // 浏览器会自动设置为 multipart/form-data; boundary=...
        } else if (config.data instanceof FormData) {
          // 如果已经是 FormData，浏览器会自动设置正确的 Content-Type 和 boundary
          // 不设置 Content-Type，让浏览器自动处理
        } else {
          // 其他情况使用 JSON
          config.headers['Content-Type'] = 'application/json'
        }
      }

      return config
    },
    (error) => {
      // 记录请求错误日志
      httpLogger.error(error)
      return Promise.reject(error)
    }
  )

  // 响应拦截器
  axios.interceptors.response.use(
    (response: AxiosResponse) => {
      // 记录响应日志
      httpLogger.response(response)

      // HTTP 状态码 < 400 表示成功，直接返回响应数据
      return response.data
    },
    async (error: AxiosError) => {
      // 记录错误日志
      httpLogger.error(error)

      const originalRequest = error.config as InternalAxiosRequestConfig & {
        _retry?: boolean
      }

      // 处理 401 错误（token 过期）
      if (error.response?.status === 401 && !originalRequest._retry) {
        // 避免刷新 token 接口本身返回 401 时的循环
        if (originalRequest.url?.includes('/auth/refresh')) {
          ElMessage.error('登录已过期，请重新登录')
          localStorage.removeItem('access_token')
          localStorage.removeItem('refresh_token')
          router.push('/login')
          return Promise.reject(error)
        }

        // 标记为重试，避免无限循环
        originalRequest._retry = true

        try {
          // 多个请求同时触发时，会共享同一个 refreshPromise
          const newToken = await refreshToken()

          // 更新请求头并重试
          originalRequest.headers.Authorization = `Bearer ${newToken}`
          return axios(originalRequest)
        } catch (refreshError) {
          // 刷新失败，跳转到登录页
          ElMessage.error('登录已过期，请重新登录')
          router.push('/login')
          return Promise.reject(refreshError)
        }
      }

      // 处理其他网络错误
      if (error.response) {
        const { status, data } = error.response
        ElMessage.error((data as any)?.message || `请求失败: ${status}`)
      } else if (error.request) {
        ElMessage.error('网络错误，请检查网络连接')
      } else {
        ElMessage.error('请求配置错误')
      }

      return Promise.reject(error)
    }
  )
}

// 自动初始化 axios 配置
setupAxios()

// 导出配置好的 axios 实例供其他模块使用
export const request = axios

// API 响应类型定义
export interface ApiResponse<T = any> {
  code: number
  message: string
  data: T
}

// 分页请求参数
export interface PageReq {
  page: number
  page_size: number
}

// 分页响应数据
export interface PageRes<T> {
  list: T[]
  total: number
  page: number
  page_size: number
}

export default axios
